import { motion } from 'framer-motion'
import { Activity, CheckCircle, Clock, Search, TrendingUp, Upload } from 'lucide-react'

/**
 * KPI 指標卡片組件
 * 顯示單個 KPI 指標,含圖標、標題、數值、顏色標記
 */
const KPICard = ({ icon: Icon, title, value, color, status }) => {
    const colorClasses = {
        green: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
        yellow: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
        red: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
        gray: 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
    }

    const iconColorClasses = {
        green: 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/40',
        yellow: 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/40',
        red: 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/40',
        gray: 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700'
    }

    const textColorClasses = {
        green: 'text-green-700 dark:text-green-300',
        yellow: 'text-yellow-700 dark:text-yellow-300',
        red: 'text-red-700 dark:text-red-300',
        gray: 'text-gray-700 dark:text-gray-300'
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`border rounded-lg p-4 ${colorClasses[color] || colorClasses.gray}`}
        >
            <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${iconColorClasses[color] || iconColorClasses.gray}`}>
                    <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">{title}</p>
                    <p className={`text-lg font-semibold ${textColorClasses[color] || textColorClasses.gray}`}>
                        {value || '未記錄'}
                    </p>
                    {status && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{status}</p>
                    )}
                </div>
            </div>
        </motion.div>
    )
}

/**
 * 根據耗時(秒)判斷顏色
 * @param {number} seconds - 耗時秒數
 * @param {string} type - 指標類型 ('upload' | 'analysis' | 'search' | 'total')
 * @returns {string} - 顏色類別 ('green' | 'yellow' | 'red' | 'gray')
 */
const getColorByDuration = (seconds, type) => {
    if (!seconds || seconds === 0) return 'gray'

    const thresholds = {
        upload: { fast: 10, medium: 30 },      // 上傳: <10秒快, <30秒中等, >=30秒慢
        analysis: { fast: 30, medium: 90 },    // 分析: <30秒快, <90秒中等, >=90秒慢
        search: { fast: 5, medium: 15 },       // 檢索: <5秒快, <15秒中等, >=15秒慢
        total: { fast: 60, medium: 180 }       // 總計: <1分鐘快, <3分鐘中等, >=3分鐘慢
    }

    const threshold = thresholds[type] || thresholds.total

    if (seconds < threshold.fast) return 'green'
    if (seconds < threshold.medium) return 'yellow'
    return 'red'
}

/**
 * 格式化耗時顯示
 * @param {number} seconds - 耗時秒數
 * @returns {string} - 格式化字符串 (如: "1.5秒", "2.3分鐘", "1.2小時")
 */
const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '未記錄'
    if (seconds < 60) return `${seconds.toFixed(1)}秒`
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)}分鐘`
    return `${(seconds / 3600).toFixed(1)}小時`
}

/**
 * KPI 指標區塊組件
 * 顯示完整的 KPI 指標,包含上傳、分析、檢索、總耗時 4 個卡片
 * @param {Object} kpiData - KPI 數據對象
 * @param {number} kpiData.upload_duration - 上傳耗時(秒)
 * @param {number} kpiData.analysis_duration - 分析耗時(秒)
 * @param {number} kpiData.search_duration - 檢索耗時(秒)
 * @param {number} kpiData.total_duration - 總耗時(秒)
 * @param {string} kpiData.kpi_status - KPI 狀態
 * @param {number} kpiData.search_result_count - 檢索結果數量
 */
export const KPIMetrics = ({ kpiData }) => {
    // 如果沒有 KPI 數據或者 total_duration 為 0,顯示提示信息
    if (!kpiData || !kpiData.total_duration) {
        return (
            <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                    <Activity className="h-5 w-5" />
                    <p className="text-sm font-medium">此案件暫無 KPI 追蹤數據</p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                    透過文件上傳自動創建的案件會記錄完整的處理時間數據
                </p>
            </div>
        )
    }

    const metrics = [
        {
            icon: Upload,
            title: '上傳時間',
            value: formatDuration(kpiData.upload_duration),
            color: getColorByDuration(kpiData.upload_duration, 'upload'),
            status: kpiData.upload_completed_at ? '已完成' : '進行中'
        },
        {
            icon: Activity,
            title: '分析時間',
            value: formatDuration(kpiData.analysis_duration),
            color: getColorByDuration(kpiData.analysis_duration, 'analysis'),
            status: kpiData.analysis_completed_at ? '已完成' : kpiData.analysis_started_at ? '進行中' : '未開始'
        },
        {
            icon: Search,
            title: '檢索時間',
            value: formatDuration(kpiData.search_duration),
            color: getColorByDuration(kpiData.search_duration, 'search'),
            status: kpiData.search_result_count ? `找到 ${kpiData.search_result_count} 筆結果` : '未檢索'
        },
        {
            icon: Clock,
            title: '總處理時間',
            value: formatDuration(kpiData.total_duration),
            color: getColorByDuration(kpiData.total_duration, 'total'),
            status: kpiData.kpi_status || '已完成'
        }
    ]

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-6"
        >
            <div className="flex items-center space-x-2 mb-4">
                <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    效能指標 (KPI)
                </h4>
                {kpiData.document_name && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                        · 來源: {kpiData.document_name}
                    </span>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {metrics.map((metric, index) => (
                    <KPICard
                        key={index}
                        icon={metric.icon}
                        title={metric.title}
                        value={metric.value}
                        color={metric.color}
                        status={metric.status}
                    />
                ))}
            </div>

            {/* KPI 時間軸 */}
            <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800/30 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2 mb-2">
                    <CheckCircle className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300">處理時間軸</p>
                </div>
                <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                    {kpiData.upload_started_at && (
                        <span>開始: {new Date(kpiData.upload_started_at).toLocaleString('zh-TW')}</span>
                    )}
                    {kpiData.analysis_completed_at && (
                        <>
                            <span>→</span>
                            <span>完成: {new Date(kpiData.analysis_completed_at).toLocaleString('zh-TW')}</span>
                        </>
                    )}
                </div>
            </div>
        </motion.div>
    )
}

/**
 * KPI 徽章組件 - 用於表格顯示
 * 顯示簡化版的 KPI 總耗時,適合在列表中使用
 * @param {number} totalDuration - 總耗時(秒)
 */
export const KPIBadge = ({ totalDuration, uploadDuration, analysisDuration, searchDuration }) => {
    // 如果有 total_duration，直接使用
    if (totalDuration && totalDuration > 0) {
        const color = getColorByDuration(totalDuration, 'total')
        const colorClasses = {
            green: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
            yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400',
            red: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
            gray: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
        }

        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClasses[color]}`}>
                <Clock className="h-3 w-3 mr-1" />
                {formatDuration(totalDuration)}
            </span>
        )
    }

    // 如果沒有 total_duration，計算部分耗時
    const partialDuration = (uploadDuration || 0) + (analysisDuration || 0) + (searchDuration || 0)

    if (partialDuration === 0) {
        return (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                <Clock className="h-3 w-3 mr-1" />
                未記錄
            </span>
        )
    }

    // 顯示部分耗時（帶進行中標記）
    const color = getColorByDuration(partialDuration, 'total')
    const colorClasses = {
        green: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 border border-green-300',
        yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400 border border-yellow-300',
        red: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 border border-red-300',
        gray: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 border border-gray-300'
    }

    return (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClasses[color]}`}>
            <Clock className="h-3 w-3 mr-1" />
            {formatDuration(partialDuration)}
            <span className="ml-1 text-[10px] opacity-70">進行中</span>
        </span>
    )
}

export default KPIMetrics
